
import React, { useState } from 'react';

const InvestmentApplicationForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    investmentAmount: '',
    investmentExperience: '',
    investmentGoals: '',
    timeframe: '',
    riskTolerance: '',
    liquidNetWorth: '',
    annualIncome: '',
    investmentType: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d42hb66mj2r470qad2ng', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          investmentAmount: '',
          investmentExperience: '',
          investmentGoals: '',
          timeframe: '',
          riskTolerance: '',
          liquidNetWorth: '',
          annualIncome: '',
          investmentType: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <form onSubmit={handleSubmit} data-readdy-form id="investment-application">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="md:col-span-2">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
          </div>
          
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>

          {/* Investment Information */}
          <div className="md:col-span-2 mt-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Investment Information</h3>
          </div>

          <div>
            <label htmlFor="investmentAmount" className="block text-sm font-medium text-gray-700 mb-2">
              Intended Investment Amount *
            </label>
            <select
              id="investmentAmount"
              name="investmentAmount"
              value={formData.investmentAmount}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Amount</option>
              <option value="$50,000 - $100,000">$50,000 - $100,000</option>
              <option value="$100,000 - $250,000">$100,000 - $250,000</option>
              <option value="$250,000 - $500,000">$250,000 - $500,000</option>
              <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
              <option value="$1,000,000+">$1,000,000+</option>
            </select>
          </div>

          <div>
            <label htmlFor="investmentType" className="block text-sm font-medium text-gray-700 mb-2">
              Investment Type Interest *
            </label>
            <select
              id="investmentType"
              name="investmentType"
              value={formData.investmentType}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Type</option>
              <option value="Single Property Investment">Single Property Investment</option>
              <option value="Portfolio Investment">Portfolio Investment</option>
              <option value="Development Partnership">Development Partnership</option>
              <option value="Franchise Opportunity">Franchise Opportunity</option>
            </select>
          </div>

          <div>
            <label htmlFor="investmentExperience" className="block text-sm font-medium text-gray-700 mb-2">
              Investment Experience *
            </label>
            <select
              id="investmentExperience"
              name="investmentExperience"
              value={formData.investmentExperience}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Experience</option>
              <option value="First-time investor">First-time investor</option>
              <option value="Some experience (1-3 investments)">Some experience (1-3 investments)</option>
              <option value="Experienced (4-10 investments)">Experienced (4-10 investments)</option>
              <option value="Very experienced (10+ investments)">Very experienced (10+ investments)</option>
            </select>
          </div>

          <div>
            <label htmlFor="timeframe" className="block text-sm font-medium text-gray-700 mb-2">
              Investment Timeframe *
            </label>
            <select
              id="timeframe"
              name="timeframe"
              value={formData.timeframe}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Timeframe</option>
              <option value="1-2 years">1-2 years</option>
              <option value="3-5 years">3-5 years</option>
              <option value="5-10 years">5-10 years</option>
              <option value="10+ years">10+ years</option>
            </select>
          </div>

          {/* Financial Information */}
          <div className="md:col-span-2 mt-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Financial Information</h3>
          </div>

          <div>
            <label htmlFor="annualIncome" className="block text-sm font-medium text-gray-700 mb-2">
              Annual Income *
            </label>
            <select
              id="annualIncome"
              name="annualIncome"
              value={formData.annualIncome}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Income Range</option>
              <option value="$100,000 - $200,000">$100,000 - $200,000</option>
              <option value="$200,000 - $500,000">$200,000 - $500,000</option>
              <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
              <option value="$1,000,000+">$1,000,000+</option>
            </select>
          </div>

          <div>
            <label htmlFor="liquidNetWorth" className="block text-sm font-medium text-gray-700 mb-2">
              Liquid Net Worth *
            </label>
            <select
              id="liquidNetWorth"
              name="liquidNetWorth"
              value={formData.liquidNetWorth}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Net Worth Range</option>
              <option value="$250,000 - $500,000">$250,000 - $500,000</option>
              <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
              <option value="$1,000,000 - $5,000,000">$1,000,000 - $5,000,000</option>
              <option value="$5,000,000+">$5,000,000+</option>
            </select>
          </div>

          <div>
            <label htmlFor="riskTolerance" className="block text-sm font-medium text-gray-700 mb-2">
              Risk Tolerance *
            </label>
            <select
              id="riskTolerance"
              name="riskTolerance"
              value={formData.riskTolerance}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
            >
              <option value="">Select Risk Tolerance</option>
              <option value="Conservative">Conservative</option>
              <option value="Moderate">Moderate</option>
              <option value="Aggressive">Aggressive</option>
              <option value="Very Aggressive">Very Aggressive</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="investmentGoals" className="block text-sm font-medium text-gray-700 mb-2">
              Investment Goals *
            </label>
            <textarea
              id="investmentGoals"
              name="investmentGoals"
              value={formData.investmentGoals}
              onChange={handleInputChange}
              required
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
              placeholder="Please describe your investment goals and what you hope to achieve..."
            />
            <div className="text-xs text-gray-500 mt-1">
              {formData.investmentGoals.length}/500 characters
            </div>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Information
            </label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
              placeholder="Any additional information you'd like to share..."
            />
            <div className="text-xs text-gray-500 mt-1">
              {formData.additionalInfo.length}/500 characters
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting Application...' : 'Submit Investment Application'}
          </button>
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex">
              <i className="ri-check-circle-fill text-green-500 text-xl mr-3"></i>
              <div>
                <h4 className="text-green-800 font-semibold">Application Submitted Successfully!</h4>
                <p className="text-green-700 text-sm mt-1">
                  Thank you for your interest. Our investment team will review your application and contact you within 48 hours.
                </p>
              </div>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <i className="ri-error-warning-fill text-red-500 text-xl mr-3"></i>
              <div>
                <h4 className="text-red-800 font-semibold">Submission Failed</h4>
                <p className="text-red-700 text-sm mt-1">
                  There was an error submitting your application. Please try again or contact us directly.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-gray-600 text-center">
          <p>
            By submitting this form, you agree to our privacy policy and terms of service. 
            All information will be kept confidential and used solely for investment evaluation purposes.
          </p>
        </div>
      </form>
    </div>
  );
};

export default InvestmentApplicationForm;
